import { useCallback, useEffect, useState } from "react";
import { AddButton, AddLink } from "../design/AddLink";
import { Link, useNavigate, useParams } from "react-router-dom";

import { send } from "../api/auth";
import { console_, type Client, type ClientUser, type RoleOption } from "../api/console";
import { LoadFailed } from "../design/LoadFailed";
import { StatusChip } from "../design/StatusChip";
import { toneOf, type Tone } from "../design/tone";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import type { Failure } from "../system/failure";
import { when } from "../system/format";

// ---------------------------------------------------------------------------
// Client accounts, in the console.
//
// THREE RULES THIS FILE FOLLOWS THAT ARE EASY TO BREAK.
//
// 1. A CONTROL THE SERVER WOULD REFUSE IS NOT SHOWN — and hiding it is not the
//    security boundary. `may()` is advisory: the API refuses the request and
//    the database refuses the row. This only avoids offering somebody a door
//    that will be shut in their face.
//
// 2. STATUS IS NEVER COLOUR ALONE. Every state below goes through StatusChip,
//    which derives its icon from the tone so a caller cannot omit it, and
//    refuses an empty label. This is read on a cracked phone, in the rain, at
//    06:00 — and on a projector, by somebody with a colour-vision difference.
//
// 3. THE ROLE LIST COMES FROM THE SERVER. There is no <option> written here.
//    Absolute rule 4: a vocabulary hard-coded in the browser is a second source
//    of truth that stops agreeing with the database silently.
//
// ARCHIVED, NOT DELETED, and the word on the button says so. V22's Proof Packs
// and V32's invoices point at these rows; a button labelled "Delete" that
// archives is a lie, and one that really deleted would break evidence.
// ---------------------------------------------------------------------------

// A failed read keeps its status, so a permission limit and a fault read differently (design/LoadFailed).
type Loaded<T> = { state: "loading" } | { state: "error"; failure: Failure } | { state: "ready"; data: T };

function useRoles(): RoleOption[] {
  const { apiBase } = useSession();
  const [roles, setRoles] = useState<RoleOption[]>([]);
  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const { status, body } = await send<{ client_roles?: RoleOption[] }>(console_.roles(apiBase));
        if (live && status === 200 && body.client_roles) setRoles(body.client_roles);
      } catch {
        // A failure here leaves the select empty and the form unusable, which is
        // visible. Inventing a default role would be worse: it would silently
        // grant whatever this file guessed.
      }
    })();
    return () => {
      live = false;
    };
  }, [apiBase]);
  return roles;
}

// ===========================================================================
// The list
// ===========================================================================

export function ClientList() {
  const { apiBase, me } = useSession();
  const { t, refusal, locale } = useT();
  const [showArchived, setShowArchived] = useState(false);
  const [loaded, setLoaded] = useState<Loaded<Client[]>>({ state: "loading" });
  // S15. Search and the next page: the list stopped at 50 with no sign that it had.
  const [typed, setTyped] = useState("");
  const [search, setSearch] = useState("");
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  // The real total from the server (UX pass, 22 October 2026: "1-50 of 1 234", never only "Show more").
  const [total, setTotal] = useState<number | null>(null);
  useEffect(() => {
    const t = window.setTimeout(() => setSearch(typed.trim()), 250);
    return () => window.clearTimeout(t);
  }, [typed]);

  const load = useCallback(
    async (cursor?: string) => {
      if (!cursor) setLoaded({ state: "loading" });
      try {
        const { status, body } = await send<{
          clients?: Client[];
          next_cursor?: string | null;
          total?: number;
          error?: { message?: string };
        }>(console_.listClients(apiBase, { archived: showArchived, ...(search ? { q: search } : {}), ...(cursor ? { cursor } : {}) }));
        if (status !== 200 || !body.clients) {
          setLoaded({ state: "error", failure: { status, refusal: refusal(body.error) } });
          return;
        }
        const page = body.clients;
        setLoaded((prev) => ({ state: "ready", data: cursor && prev.state === "ready" ? [...prev.data, ...page] : page }));
        setNextCursor(body.next_cursor ?? null);
        setTotal(typeof body.total === "number" ? body.total : null);
      } catch {
        setLoaded({ state: "error", failure: { status: 0, refusal: t("We could not reach Verbunk.") } });
      }
    },
    [apiBase, showArchived, search],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const mayManage = me?.authenticated === true && me.permissions.includes("client.manage");

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t("Clients")}</h2>
        {mayManage ? (
          <AddLink to="/console/clients/new" label={t("Add a client")} />
        ) : null}
      </div>

      <div className="row">
        <label className="visually-hidden" htmlFor="client-search">
          {t("Search clients")}
        </label>
        <input
          id="client-search"
          type="search"
          placeholder={t("Search by name or reference")}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
        />
        <label className="check">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
          {t("Show archived")}
        </label>
      </div>

      {loaded.state === "loading" ? <p className="muted">{t("Loading…")}</p> : null}

      {loaded.state === "error" ? (
        <LoadFailed failure={loaded.failure} fallback={t("We could not load your clients.")} onRetry={() => void load()} />
      ) : null}

      {loaded.state === "ready" && loaded.data.length === 0 ? (
        <p className="muted">
          {t("No clients yet. A client account is who the work is for — events, rates and the portal all hang off one.")}
        </p>
      ) : null}

      {loaded.state === "ready" && loaded.data.length > 0 ? (
        <table className="data-table">
          <caption className="visually-hidden">{t("Client accounts in this organisation")}</caption>
          <thead>
            <tr>
              <th scope="col">{t("Name")}</th>
              <th scope="col">{t("Reference")}</th>
              <th scope="col">{t("Next event")}</th>
            </tr>
          </thead>
          <tbody>
            {loaded.data.map((client) => (
              <tr key={client.id}>
                <th scope="row">
                  <Link to={`/console/clients/${client.id}`}>{client.name}</Link>
                  {client.portal_enabled ? <StatusChip tone="live" label={t("Portal")} /> : null}
                  {client.status_tone !== "live" ? (
                    <StatusChip tone={toneOf(client.status_tone)} label={translateLabel(locale, client.status_label)} />
                  ) : null}
                </th>
                <td className="numeric">{client.reference ?? "—"}</td>
                <td>
                  {client.next_event ? (
                    <Link to={`/console/events/${client.next_event.id}`}>
                      {client.next_event.name} · {when(client.next_event.starts_at)}
                    </Link>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {loaded.state === "ready" && total !== null && loaded.data.length > 0 ? (
        <p className="muted">{t("{shown} of {total} clients", { shown: loaded.data.length, total })}</p>
      ) : null}
      {nextCursor ? (
        <button type="button" className="btn btn-quiet" onClick={() => void load(nextCursor)}>
          {t("Show more")}
        </button>
      ) : null}
    </section>
  );
}

// ===========================================================================
// Create
// ===========================================================================

/** Tour T-17, T-19. The client's editable fields, shared by Add and Edit. */
type ClientDraft = {
  name: string;
  reference: string;
  billing_email: string;
  billing_address: string;
  vat_number: string;
  payment_terms_days: string;
  purchase_order_required: boolean;
};

function draftOf(c?: Client): ClientDraft {
  return {
    name: c?.name ?? "",
    reference: c?.reference ?? "",
    billing_email: c?.billing_email ?? "",
    billing_address: c?.billing_address ?? "",
    vat_number: c?.vat_number ?? "",
    payment_terms_days: String(c?.payment_terms_days ?? 30),
    purchase_order_required: c?.purchase_order_required ?? false,
  };
}

function payloadOf(d: ClientDraft) {
  return {
    name: d.name,
    reference: d.reference || null,
    billing_email: d.billing_email || null,
    billing_address: d.billing_address || null,
    vat_number: d.vat_number || null,
    payment_terms_days: Number(d.payment_terms_days),
    purchase_order_required: d.purchase_order_required,
  };
}

function ClientFieldsets({ draft, onChange }: { draft: ClientDraft; onChange: (d: ClientDraft) => void }) {
  const { t } = useT();
  const set = <K extends keyof ClientDraft>(key: K, value: ClientDraft[K]) => onChange({ ...draft, [key]: value });
  return (
    <>
      <fieldset className="form-section">
        <div className="form-grid">
          <div className="field">
            <label htmlFor="client-name">{t("Client name")}</label>
            <input id="client-name" type="text" required value={draft.name} onChange={(e) => set("name", e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="client-reference">{t("Your reference")}</label>
            <input id="client-reference" type="text" value={draft.reference} onChange={(e) => set("reference", e.target.value)} />
            <p className="field-hint">{t("Optional. Your own code for this client — it must be unique.")}</p>
          </div>
        </div>
      </fieldset>

      <details
        className="form-section"
        open={Boolean(draft.billing_email || draft.billing_address || draft.vat_number || draft.payment_terms_days !== "30" || draft.purchase_order_required)}
      >
        <summary>{t("Billing (optional)")}</summary>
        <div className="form-grid">
          <div className="field">
            <label htmlFor="client-billing-email">{t("Billing email")}</label>
            <input id="client-billing-email" type="email" value={draft.billing_email} onChange={(e) => set("billing_email", e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="client-vat">{t("VAT number")}</label>
            <input id="client-vat" type="text" value={draft.vat_number} onChange={(e) => set("vat_number", e.target.value)} />
          </div>
          <div className="field form-grid-wide">
            <label htmlFor="client-billing-address">{t("Billing address")}</label>
            <textarea id="client-billing-address" rows={Math.max(2, draft.billing_address.split("\n").length)} value={draft.billing_address} onChange={(e) => set("billing_address", e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="client-terms">{t("Payment terms (days)")}</label>
            <input
              id="client-terms"
              type="number"
              min={0}
              max={180}
              required
              value={draft.payment_terms_days}
              onChange={(e) => set("payment_terms_days", e.target.value)}
            />
          </div>
          <label className="check">
            <input
              type="checkbox"
              checked={draft.purchase_order_required}
              onChange={(e) => set("purchase_order_required", e.target.checked)}
            />
            {t("A purchase order is required")}
          </label>
        </div>
      </details>
    </>
  );
}

export function NewClient() {
  const { apiBase } = useSession();
  const { t, refusal } = useT();
  const navigate = useNavigate();
  // Tour T-17 (owner, 21 October 2026): billing in the same form, all optional.
  const [draft, setDraft] = useState<ClientDraft>(draftOf());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { status, body } = await send<{ client?: Client; error?: { message?: string } }>(
        console_.createClient(apiBase, payloadOf(draft)),
      );
      if (status !== 201 || !body.client) {
        setError(refusal(body.error) ?? t("That did not work. Please try again."));
        return;
      }
      navigate(`/console/clients/${body.client.id}`, { replace: true });
    } catch {
      setError(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t("Add a client")}</h2>
      </div>

      <div className="form-message" role="status" aria-live="polite">
        {error ? (
          <>
            <StatusChip tone="failed" label={t("Not saved")} />
            <span>{error}</span>
          </>
        ) : null}
      </div>

      <form onSubmit={submit}>
        <ClientFieldsets draft={draft} onChange={setDraft} />

        <div className="row">
          <button className="btn" type="submit" disabled={busy}>
            {busy ? t("Working…") : t("Add client")}
          </button>
          <Link className="btn btn-quiet" to="/console/clients">
            {t("Cancel")}
          </Link>
        </div>
      </form>
    </section>
  );
}

// ===========================================================================
// Detail
// ===========================================================================

export function ClientDetail() {
  const { apiBase, me } = useSession();
  const { t, refusal, locale } = useT();
  const { id = "" } = useParams();
  const roles = useRoles();
  const [loaded, setLoaded] = useState<Loaded<{ client: Client; users: ClientUser[] }>>({ state: "loading" });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: Tone; status: string; message: string } | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [inviteRole, setInviteRole] = useState("");
  // Tour T-19 (owner, 21 October 2026): the details card edits in place.
  const [editing, setEditing] = useState<ClientDraft | null>(null);

  const load = useCallback(async () => {
    try {
      const { status, body } = await send<{
        client?: Client;
        users?: ClientUser[];
        error?: { message?: string };
      }>(console_.getClient(apiBase, id));
      if (status !== 200 || !body.client) {
        setLoaded({ state: "error", failure: { status, refusal: refusal(body.error) } });
        return;
      }
      setLoaded({ state: "ready", data: { client: body.client, users: body.users ?? [] } });
    } catch {
      setLoaded({ state: "error", failure: { status: 0, refusal: t("We could not reach Verbunk.") } });
    }
  }, [apiBase, id]);

  useEffect(() => {
    void load();
  }, [load]);

  const mayManage = me?.authenticated === true && me.permissions.includes("client.manage");

  async function act(spec: Parameters<typeof send>[0], success: string): Promise<boolean> {
    if (busy) return false;
    setBusy(true);
    setNotice(null);
    try {
      const { status, body } = await send<{ error?: { message?: string } }>(spec);
      if (status >= 400) {
        setNotice({ tone: "failed", status: t("Not saved"), message: refusal(body.error) ?? t("That did not work.") });
        return false;
      }
      setNotice({ tone: "live", status: t("Saved"), message: success });
      await load();
      return true;
    } catch {
      setNotice({ tone: "attention", status: t("No connection"), message: t("We could not reach Verbunk.") });
      return false;
    } finally {
      setBusy(false);
    }
  }

  if (loaded.state === "loading") return <p className="muted">{t("Loading…")}</p>;
  if (loaded.state === "error") {
    return (
      <section className="panel">
        <div className="panel-head">
          <h2>{t("Client")}</h2>
        </div>
        <LoadFailed failure={loaded.failure} fallback={t("We could not load that client.")} onRetry={() => void load()} />
        <Link className="btn btn-secondary" to="/console/clients">
          {t("Back to clients")}
        </Link>
      </section>
    );
  }

  const { client, users } = loaded.data;

  return (
    <div className="stack">
      <section className="panel">
        <div className="panel-head">
          <h2>{client.name}</h2>
          <StatusChip tone={toneOf(client.status_tone)} label={translateLabel(locale, client.status_label)} />
          {mayManage && editing === null ? (
            <button type="button" className="btn btn-secondary panel-head-end" onClick={() => setEditing(draftOf(client))}>
              {t("Edit")}
            </button>
          ) : null}
        </div>

        <div className="form-message" role="status" aria-live="polite">
          {notice ? (
            <>
              <StatusChip tone={notice.tone} label={notice.status} />
              <span>{notice.message}</span>
            </>
          ) : null}
        </div>

        {editing !== null ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void act(console_.updateClient(apiBase, client.id, payloadOf(editing)), t("The client's details are saved.")).then(
                (saved) => {
                  if (saved) setEditing(null);
                },
              );
            }}
          >
            <ClientFieldsets draft={editing} onChange={setEditing} />
            <div className="row">
              <button className="btn" type="submit" disabled={busy}>
                {busy ? t("Working…") : t("Save")}
              </button>
              <button className="btn btn-quiet" type="button" onClick={() => setEditing(null)}>
                {t("Cancel")}
              </button>
            </div>
          </form>
        ) : (
        <dl className="facts">
          <dt>{t("Reference")}</dt>
          <dd className="numeric">{client.reference ?? "—"}</dd>
          <dt>{t("Billing email")}</dt>
          <dd>{client.billing_email ?? "—"}</dd>
          <dt>{t("Billing address")}</dt>
          <dd className="preserve-lines">{client.billing_address ?? "—"}</dd>
          <dt>{t("VAT number")}</dt>
          <dd className="numeric">{client.vat_number ?? "—"}</dd>
          <dt>{t("Payment terms")}</dt>
          <dd className="numeric">{t("{n} days", { n: client.payment_terms_days })}</dd>
          <dt>{t("Purchase order")}</dt>
          <dd>{client.purchase_order_required ? t("Required") : t("Not required")}</dd>
          <dt>{t("Client portal")}</dt>
          <dd>
            <StatusChip
              tone={client.portal_enabled ? "live" : "neutral"}
              label={client.portal_enabled ? t("On") : t("Off")}
            />
          </dd>
        </dl>
        )}

        {mayManage && editing === null ? (
          <div className="row">
            <button
              className="btn btn-secondary"
              type="button"
              disabled={busy}
              onClick={() =>
                void act(
                  console_.updateClient(apiBase, client.id, { portal_enabled: !client.portal_enabled }),
                  client.portal_enabled ? t("The client portal is off.") : t("The client portal is on."),
                )
              }
            >
              {client.portal_enabled ? t("Turn the portal off") : t("Turn the portal on")}
            </button>
            {/* S41 (0194): the one move from this state, worded by the database; the
                button sends back the status the server named. */}
            {client.move ? (
              <button
                className="btn btn-quiet"
                type="button"
                disabled={busy}
                onClick={() => {
                  const move = client.move;
                  if (move) void act(console_.updateClient(apiBase, client.id, { status: move.status }), translateLabel(locale, move.done));
                }}
              >
                {translateLabel(locale, client.move.label)}
              </button>
            ) : null}
          </div>
        ) : null}
      </section>

      <section className="panel">
        <div className="panel-head">
          <h2>{t("People at this client")}</h2>
          {mayManage ? <AddButton label={t("Invite someone by email")} expanded={inviting} onClick={() => setInviting(!inviting)} /> : null}
        </div>

        <p className="muted">
          {t("Someone invited here can sign in to the client portal and see only this client’s events. Access starts when they accept the emailed invitation, not when it is sent.")}
        </p>

        {users.length === 0 ? (
          <p className="muted">{t("Nobody yet.")}</p>
        ) : (
          <table className="data-table">
            <caption className="visually-hidden">{t("People with access to this client account")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("Person")}</th>
                <th scope="col">{t("Role")}</th>
                <th scope="col">{t("Status")}</th>
                <th scope="col">
                  <span className="visually-hidden">{t("Actions")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  {/* The account id and not an address: app.user_account is not
                      readable by the tenant role at all, by design (V3). */}
                  <th scope="row" className="numeric">
                    {user.user_account_id.slice(0, 8)}
                  </th>
                  <td>
                    {mayManage && !user.is_withdrawn ? (
                      <select
                        aria-label={t("Role")}
                        value={user.role}
                        disabled={busy}
                        onChange={(e) =>
                          void act(
                            console_.setClientUserRole(apiBase, client.id, user.id, e.target.value),
                            t("Their role is changed."),
                          )
                        }
                      >
                        {roles.map((r) => (
                          <option key={r.id} value={r.id}>
                            {translateLabel(locale, r.label)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      translateLabel(locale, roles.find((r) => r.id === user.role)?.label ?? user.role)
                    )}
                  </td>
                  <td>
                    {/* S33: words, tone and is_withdrawn are the status row's (0172). */}
                    <StatusChip tone={toneOf(user.status_tone)} label={translateLabel(locale, user.status_label)} />
                  </td>
                  <td>
                    {mayManage && !user.is_withdrawn ? (
                      <button
                        className="btn btn-quiet"
                        type="button"
                        disabled={busy}
                        onClick={() =>
                          void act(
                            console_.revokeClientUser(apiBase, client.id, user.id),
                            t("That person no longer has access."),
                          )
                        }
                      >
                        {t("Withdraw access")}
                      </button>
                    ) : mayManage ? (
                      // S30b. No address on this row to resend to (V3), so the way
                      // back is the form below, which reopens this same row.
                      <span className="muted">{t("To give access back, invite their address again with the plus.")}</span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {mayManage && inviting ? (
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              if (inviteRole === "") return;
              void act(
                console_.inviteClientUser(apiBase, client.id, inviteEmail, inviteRole),
                t("The invitation is on its way."),
              ).then(() => {
                setInviteEmail("");
                setInviting(false);
              });
            }}
          >
            <div className="field">
              <label htmlFor="client-invite-email">{t("Invite someone by email")}</label>
              <input
                id="client-invite-email"
                type="email"
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                required
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="client-invite-role">{t("Role")}</label>
              {/* No default, for the reason set out in People.tsx: the answer
                  to "what may this person see" is asked, never assumed. */}
              <select
                id="client-invite-role"
                required
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
              >
                <option value="">{t("Choose a role…")}</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {translateLabel(locale, role.label)}
                  </option>
                ))}
              </select>
              <p className="field-hint">
                {roles.find((r) => r.id === inviteRole)?.description ??
                  t("Choose what this person will be able to see.")}
              </p>
            </div>
            <div className="row">
              <button className="btn" type="submit" disabled={busy || inviteRole === ""}>
                {busy ? t("Working…") : t("Send invitation")}
              </button>
              <button className="btn btn-quiet" type="button" onClick={() => setInviting(false)}>
                {t("Cancel")}
              </button>
            </div>
          </form>
        ) : null}
      </section>

      <div className="row">
        <Link className="btn btn-quiet" to="/console/clients">
          {t("Back to clients")}
        </Link>
      </div>
    </div>
  );
}
